import { Controller } from "@hotwired/stimulus"

// Indies Cut-In Report (card #2162, WIN Article #179). The server ships
// every Independent Grocery MissionCycle's (monthly AND weekly child)
// precomputed, already-vk-deduplicated cut_in_by_store/cut_in_by_rep/
// cut_in_by_client/cut_in_by_store_rep rows (each split by Field/Phone/Both
// source scope) as one JSON payload (see #connect) -- from there this
// controller owns ALL interactive state (view, filters, sort, CSV export)
// with zero further round-trips, mirroring
// indies_cycle_summary_controller.js's "ship once, render client-side"
// architecture.
//
// By Store and By Person are BOTH always computed from the finer-grained
// cut_in_by_store_rep rows (place_id + user_id), not from the coarser
// cut_in_by_store/cut_in_by_rep totals directly -- this is what lets place-
// attribute filters (State/Territory/Frequency/Rank/Store search/Place
// Owner) correctly narrow the By Person view, and lets the Person filter
// correctly narrow the By Store view, without needing a second cross-
// dimensional table. By Client has no place cross-dimension in this data
// model, so place-attribute and Person filters do not narrow it -- only the
// Client and Source filters and the Strike Rate bucket apply there. This is
// a deliberate, disclosed scope reduction from Article #179's original
// flat-array design (see card #2162 build notes).
//
// Period selection: Article #179's original Date filter (individual visit
// dates, This Week/Last Week/CTD presets) is replaced with week-level
// granularity -- the server ships weekly-child MissionCycle data
// (`weeks`) alongside monthly cycle data (`cycles`), same split
// IndiesCycleSummaryQuery already uses. This Week/Last Week/CTD read
// directly from `weeks`/`cycles` rather than needing a per-date dimension.
// Known gap: week 1 of every month never has data (a pre-existing bug in
// #2345's MissionCycleSummarySnapshotBuilder collides week 1's snapshot
// with its monthly parent's -- flagged separately, not fixed here).
export default class extends Controller {
  static targets = ["headerBadges", "filterBar", "viewTabs", "resultCount", "tableContainer", "payload"]
  static values = { pendingNote: String }

  strikeRateBuckets() {
    return [
      { key: "75plus", label: "75%+", test: (pct) => pct !== null && pct >= 75 },
      { key: "50to75", label: "50-75%", test: (pct) => pct !== null && pct >= 50 && pct < 75 },
      { key: "25to50", label: "25-50%", test: (pct) => pct !== null && pct >= 25 && pct < 50 },
      { key: "under25", label: "<25%", test: (pct) => pct !== null && pct > 0 && pct < 25 },
      { key: "zero", label: "0%", test: (pct) => pct === 0 }
    ]
  }

  connect() {
    this.data = this.readPayload()

    this.state = {
      view: "store",
      filters: {
        source: "field",
        granularity: "cycle",
        cycle: "all",
        week: "all",
        person: "all",
        state: "all",
        territory: "both",
        client: "all",
        placeOwner: "all",
        frequency: "all",
        rankFrom: "",
        rankTo: "",
        strikeRate: [],
        store: ""
      },
      sortKey: { store: "store", person: "stores", client: "cut_ins" },
      sortDir: { store: "asc", person: "desc", client: "desc" }
    }

    if (!this.data.cycles || this.data.cycles.length === 0) {
      this.renderPending()
      return
    }

    this.renderFilterBar()
    this.render()
  }

  readPayload() {
    const empty = { cycles: [], weeks: [], places: {}, taskers: {}, clients: {}, place_owners: {}, place_owner_of: {} }
    if (!this.hasPayloadTarget) return empty
    const raw = this.payloadTarget.textContent
    if (!raw || !raw.trim()) return empty
    return JSON.parse(raw)
  }

  renderPending() {
    if (this.hasResultCountTarget) this.resultCountTarget.textContent = this.pendingNoteValue || "No data yet."
    if (this.hasTableContainerTarget) {
      this.tableContainerTarget.innerHTML = `<div class="p-8 text-center text-sm text-gray-400">${this.pendingNoteValue || "No data yet."}</div>`
    }
  }

  // ---------- view + filter selection ----------

  selectView(event) {
    this.state.view = event.currentTarget.dataset.view
    this.renderFilterBar()
    this.render()
  }

  setSegFilter(event) {
    const { filterKey, filterValue } = event.currentTarget.dataset
    this.state.filters[filterKey] = filterValue
    this.renderFilterBar()
    this.render()
  }

  selectPeriodPreset(event) {
    const preset = event.currentTarget.dataset.preset

    if (preset === "all") {
      this.state.filters.granularity = "cycle"
      this.state.filters.cycle = "all"
      this.state.filters.week = "all"
    } else if (preset === "ctd") {
      const ctd = this.ctdCycleEntry()
      this.state.filters.granularity = "cycle"
      this.state.filters.cycle = ctd ? String(ctd.cycle_id) : "all"
      this.state.filters.week = "all"
    } else if (preset === "this_week") {
      const week = this.thisWeekEntry()
      if (week) {
        this.state.filters.granularity = "week"
        this.state.filters.week = String(week.cycle_id)
      }
    } else if (preset === "last_week") {
      const week = this.lastWeekEntry()
      if (week) {
        this.state.filters.granularity = "week"
        this.state.filters.week = String(week.cycle_id)
      }
    }

    this.renderFilterBar()
    this.render()
  }

  selectMonth(event) {
    this.state.filters.granularity = "cycle"
    this.state.filters.cycle = event.currentTarget.value
    this.state.filters.week = "all"
    this.render()
  }

  toggleStrikeRateFilter(event) {
    const value = event.currentTarget.dataset.filterValue
    const current = this.state.filters.strikeRate

    if (value === "all") {
      this.state.filters.strikeRate = []
    } else if (current.includes(value)) {
      this.state.filters.strikeRate = current.filter((v) => v !== value)
    } else {
      this.state.filters.strikeRate = [...current, value]
    }
    this.renderFilterBar()
    this.render()
  }

  setSelectFilter(event) {
    this.state.filters[event.currentTarget.dataset.filterKey] = event.currentTarget.value
    this.render()
  }

  setRankFilter(event) {
    this.state.filters[event.currentTarget.dataset.filterKey] = event.currentTarget.value
    this.render()
  }

  setStoreSearch(event) {
    this.state.filters.store = event.currentTarget.value
    this.render()
  }

  setSort(event) {
    const key = event.currentTarget.dataset.sortKey
    const view = this.state.view

    if (this.state.sortKey[view] === key) {
      this.state.sortDir[view] = this.state.sortDir[view] === "asc" ? "desc" : "asc"
    } else {
      this.state.sortKey[view] = key
      this.state.sortDir[view] = this.isTextSortKey(key) ? "asc" : "desc"
    }
    this.render()
  }

  isTextSortKey(key) {
    return ["store", "rep", "client", "state"].includes(key)
  }

  exportCsv() {
    const csv = this.buildCsv()
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" })
    const url = URL.createObjectURL(blob)
    const link = document.createElement("a")
    link.href = url
    link.download = `cut-ins-${this.activePeriodLabel().replace(/\s+/g, "-")}-${new Date().toISOString().slice(0, 10)}.csv`
    link.click()
    URL.revokeObjectURL(url)
  }

  // ---------- period / cycle selection ----------

  sortedWeeks() {
    return [...(this.data.weeks || [])].sort((a, b) => a.period_start.localeCompare(b.period_start))
  }

  thisWeekEntry() {
    const weeks = this.sortedWeeks()
    return weeks.find((w) => w.to_date) || weeks[weeks.length - 1]
  }

  lastWeekEntry() {
    const weeks = this.sortedWeeks()
    const thisWeek = this.thisWeekEntry()
    if (!thisWeek) return null
    const index = weeks.findIndex((w) => w.cycle_id === thisWeek.cycle_id)
    return index > 0 ? weeks[index - 1] : null
  }

  ctdCycleEntry() {
    return this.data.cycles.find((c) => c.to_date) || null
  }

  activeCycles() {
    if (this.state.filters.granularity === "week") {
      if (this.state.filters.week === "all") return this.data.weeks || []
      const week = (this.data.weeks || []).find((w) => String(w.cycle_id) === String(this.state.filters.week))
      return week ? [week] : []
    }

    if (this.state.filters.cycle === "all") return this.data.cycles
    const cycle = this.data.cycles.find((c) => String(c.cycle_id) === String(this.state.filters.cycle))
    return cycle ? [cycle] : []
  }

  activePeriodLabel() {
    if (this.state.filters.granularity === "week" && this.state.filters.week !== "all") {
      const week = (this.data.weeks || []).find((w) => String(w.cycle_id) === String(this.state.filters.week))
      return week ? week.label : "week"
    }
    if (this.state.filters.cycle !== "all") {
      const cycle = this.data.cycles.find((c) => String(c.cycle_id) === String(this.state.filters.cycle))
      return cycle ? cycle.label : "all-cycles"
    }
    return "all-cycles"
  }

  // ---------- place attribute helpers ----------

  placeInfo(placeId) {
    return this.data.places[String(placeId)] || {}
  }

  territoryForState(state) {
    if (!state) return "Unknown"
    if (["VIC", "SA", "WA", "NT", "TAS"].includes(state)) return "Tess"
    if (["NSW/ACT", "QLD"].includes(state)) return "Scott"
    return "Unknown"
  }

  freqLabel(freq) {
    if (freq === null || freq === undefined) return "—"
    if (freq === 28) return "4-weekly"
    if (freq === 56) return "8-weekly"
    if (freq === 84) return "12-weekly"
    if (freq === 365) return "Annual"
    return `${freq}d`
  }

  placePassesFilters(placeId) {
    const info = this.placeInfo(placeId)
    const f = this.state.filters

    if (f.state !== "all" && (info.state || "Unknown") !== f.state) return false
    if (f.territory !== "both" && this.territoryForState(info.state) !== (f.territory === "tess" ? "Tess" : "Scott")) return false

    if (f.frequency !== "all") {
      const freq = info.visit_frequency
      if (f.frequency === "28" && freq !== 28) return false
      if (f.frequency === "56" && freq !== 56) return false
      if (f.frequency === "other" && (freq === 28 || freq === 56)) return false
    }

    if (f.placeOwner !== "all") {
      const ownerId = this.data.place_owner_of[String(placeId)]
      if (String(ownerId) !== String(f.placeOwner)) return false
    }

    if (f.rankFrom !== "" && (info.priority === null || info.priority === undefined || info.priority < Number(f.rankFrom))) return false
    if (f.rankTo !== "" && (info.priority === null || info.priority === undefined || info.priority > Number(f.rankTo))) return false

    if (f.store.trim() !== "") {
      const needle = f.store.trim().toLowerCase()
      if (!(info.name || "").toLowerCase().includes(needle)) return false
    }

    return true
  }

  // ---------- aggregation across active cycles ----------

  mergedStoreRepRows() {
    const source = this.state.filters.source
    const rows = new Map()

    this.activeCycles().forEach((cycle) => {
      (cycle.by_store_rep || []).forEach((row) => {
        const metrics = row[source]
        if (!metrics) return
        if (this.state.filters.person !== "all" && String(row.user_id) !== String(this.state.filters.person)) return

        const key = `${row.place_id}|${row.user_id}`
        const existing = rows.get(key) || { place_id: row.place_id, user_id: row.user_id, visits: 0, cut_ins: 0, visits_with_cutin: 0 }
        existing.visits += metrics.visits || 0
        existing.cut_ins += metrics.cut_ins || 0
        existing.visits_with_cutin += metrics.visits_with_cutin || 0
        rows.set(key, existing)
      })
    })

    return [...rows.values()]
  }

  mergedClientRows() {
    const source = this.state.filters.source
    const rows = new Map()

    this.activeCycles().forEach((cycle) => {
      (cycle.by_client || []).forEach((row) => {
        const metrics = row[source]
        if (!metrics) return

        const existing = rows.get(row.client_id) || { client_id: row.client_id, visits: 0, cut_ins: 0, visits_with_cutin: 0, distinct_stores: 0 }
        existing.visits += metrics.visits || 0
        existing.cut_ins += metrics.cut_ins || 0
        existing.visits_with_cutin += metrics.visits_with_cutin || 0
        existing.distinct_stores += metrics.distinct_stores || 0
        rows.set(row.client_id, existing)
      })
    })

    return [...rows.values()]
  }

  strikeRatePasses(pct) {
    if (this.state.filters.strikeRate.length === 0) return true
    return this.state.filters.strikeRate.some((key) => {
      const bucket = this.strikeRateBuckets().find((b) => b.key === key)
      return bucket && bucket.test(pct)
    })
  }

  // ---------- By Store ----------

  buildStoreRows() {
    const byPlace = new Map()

    this.mergedStoreRepRows().forEach((row) => {
      if (!this.placePassesFilters(row.place_id)) return

      const existing = byPlace.get(row.place_id) || { place_id: row.place_id, visits: 0, cut_ins: 0, visits_with_cutin: 0, reps: new Map() }
      existing.visits += row.visits
      existing.cut_ins += row.cut_ins
      existing.visits_with_cutin += row.visits_with_cutin
      existing.reps.set(row.user_id, (existing.reps.get(row.user_id) || 0) + row.visits)
      byPlace.set(row.place_id, existing)
    })

    return [...byPlace.values()].map((row) => {
      const info = this.placeInfo(row.place_id)
      const primaryRep = [...row.reps.entries()].sort((a, b) => b[1] - a[1])[0]
      const pct = row.visits > 0 ? Math.round((row.visits_with_cutin / row.visits) * 1000) / 10 : null

      return {
        place_id: row.place_id,
        store: info.name || `Place ${row.place_id}`,
        state: info.state || "Unknown",
        territory: this.territoryForState(info.state),
        rank: info.priority ?? null,
        frequency: info.visit_frequency ?? null,
        rep: primaryRep ? (this.data.taskers[String(primaryRep[0])] || "Vacant") : "—",
        visits: row.visits,
        visits_with_cutin: row.visits_with_cutin,
        strike_rate: pct,
        cut_ins: row.cut_ins,
        ratio: row.visits > 0 ? row.cut_ins / row.visits : null
      }
    }).filter((row) => this.strikeRatePasses(row.strike_rate))
  }

  // ---------- By Person ----------

  buildPersonRows() {
    const byUser = new Map()

    this.mergedStoreRepRows().forEach((row) => {
      if (!this.placePassesFilters(row.place_id)) return

      const existing = byUser.get(row.user_id) || { user_id: row.user_id, visits: 0, cut_ins: 0, visits_with_cutin: 0, places: new Set(), states: new Set() }
      existing.visits += row.visits
      existing.cut_ins += row.cut_ins
      existing.visits_with_cutin += row.visits_with_cutin
      existing.places.add(row.place_id)
      existing.states.add(this.placeInfo(row.place_id).state || "Unknown")
      byUser.set(row.user_id, existing)
    })

    return [...byUser.values()].map((row) => {
      const statesList = [...row.states].sort()
      const primaryState = statesList[0]
      const pct = row.visits > 0 ? Math.round((row.visits_with_cutin / row.visits) * 1000) / 10 : null

      return {
        user_id: row.user_id,
        rep: this.data.taskers[String(row.user_id)] || "Vacant",
        territory: this.territoryForState(primaryState),
        states: statesList.join(", "),
        stores: row.places.size,
        visits: row.visits,
        visits_with_cutin: row.visits_with_cutin,
        strike_rate: pct,
        cut_ins: row.cut_ins,
        ratio: row.visits > 0 ? row.cut_ins / row.visits : null
      }
    }).filter((row) => this.strikeRatePasses(row.strike_rate))
  }

  // ---------- By Client ----------

  buildClientRows() {
    return this.mergedClientRows()
      .filter((row) => this.state.filters.client === "all" || String(row.client_id) === String(this.state.filters.client))
      .map((row) => {
        const pct = row.visits > 0 ? Math.round((row.visits_with_cutin / row.visits) * 1000) / 10 : null
        return {
          client_id: row.client_id,
          client: this.data.clients[String(row.client_id)] || `Client ${row.client_id}`,
          stores: row.distinct_stores,
          visits: row.visits,
          visits_with_cutin: row.visits_with_cutin,
          strike_rate: pct,
          cut_ins: row.cut_ins,
          ratio: row.visits > 0 ? row.cut_ins / row.visits : null
        }
      })
      .filter((row) => this.strikeRatePasses(row.strike_rate))
  }

  // ---------- colour rules ----------

  // Colour SEMANTICS (thresholds) are Article #179's, mapped onto WIN's own
  // Tailwind score palette (zenith/style_guides/color-system.md) rather
  // than the brief's raw hex -- na/0% render as neutral grey (not red), a
  // deliberate distinction the brief calls out explicitly.
  srClass(pct) {
    if (pct === null || pct === undefined) return "text-gray-400"
    if (pct >= 50) return "text-green-600 font-bold"
    if (pct >= 25) return "text-amber-600 font-bold"
    if (pct > 0) return "text-red-600 font-bold"
    return "text-gray-400"
  }

  ratioClass(ratio, natAvg) {
    if (!ratio || !natAvg) return ""
    if (ratio >= natAvg) return "text-green-600 font-bold"
    if (ratio >= natAvg * 0.75) return "text-amber-600 font-bold"
    return "text-red-600 font-bold"
  }

  nationalAverageRatio(rows) {
    const totalVisits = rows.reduce((sum, r) => sum + r.visits, 0)
    const totalCutins = rows.reduce((sum, r) => sum + r.cut_ins, 0)
    return totalVisits > 0 ? totalCutins / totalVisits : 0
  }

  // ---------- render ----------

  render() {
    this.renderHeaderBadges()
    this.renderTable()
  }

  currentRows() {
    if (this.state.view === "store") return this.buildStoreRows()
    if (this.state.view === "person") return this.buildPersonRows()
    return this.buildClientRows()
  }

  renderHeaderBadges() {
    if (!this.hasHeaderBadgesTarget) return
    const rows = this.currentRows()
    const totalVisits = rows.reduce((sum, r) => sum + r.visits, 0)
    const totalWithCutin = rows.reduce((sum, r) => sum + r.visits_with_cutin, 0)
    const totalCutins = rows.reduce((sum, r) => sum + r.cut_ins, 0)
    const strikeRate = totalVisits > 0 ? Math.round((totalWithCutin / totalVisits) * 1000) / 10 : null
    const ratio = totalVisits > 0 ? Math.round((totalCutins / totalVisits) * 100) / 100 : null
    const unitLabel = this.state.view === "store" ? "Stores" : this.state.view === "person" ? "Reps" : "Clients"

    const card = (label, value) => `
      <div class="cir-kpi-card">
        <div class="cir-kpi-label">${label}</div>
        <div class="cir-kpi-value">${value}</div>
      </div>
    `

    this.headerBadgesTarget.innerHTML = [
      card(unitLabel, rows.length),
      card("Visits", totalVisits),
      card("Visits w/ Cut-in", totalWithCutin),
      card("Strike Rate %", strikeRate === null ? "—" : `${strikeRate}%`),
      card("Cut-ins", totalCutins),
      card("Cut-ins / Visit", ratio === null ? "—" : ratio)
    ].join("")
  }

  renderTable() {
    if (!this.hasTableContainerTarget) return
    const rows = this.currentRows()
    this.lastRenderedRows = rows

    if (this.state.view === "store") this.renderStoreTable(rows)
    else if (this.state.view === "person") this.renderPersonTable(rows)
    else this.renderClientTable(rows)

    this.updateResultCount(rows)
    this.updateViewTabs()
  }

  updateViewTabs() {
    if (!this.hasViewTabsTarget) return
    this.viewTabsTarget.querySelectorAll("[data-view]").forEach((btn) => {
      btn.classList.toggle("active", btn.dataset.view === this.state.view)
    })
  }

  updateResultCount(rows) {
    if (!this.hasResultCountTarget) return
    const totalVisits = rows.reduce((sum, r) => sum + r.visits, 0)
    const totalCutins = rows.reduce((sum, r) => sum + r.cut_ins, 0)

    if (this.state.view === "store") {
      this.resultCountTarget.textContent = `Showing ${rows.length} store${rows.length === 1 ? "" : "s"} · ${totalVisits} visits · ${totalCutins} cut-ins`
    } else if (this.state.view === "person") {
      const stores = rows.reduce((sum, r) => sum + r.stores, 0)
      this.resultCountTarget.textContent = `Showing ${rows.length} rep${rows.length === 1 ? "" : "s"} · ${stores} stores · ${totalVisits} visits · ${totalCutins} cut-ins`
    } else {
      const stores = rows.reduce((sum, r) => sum + r.stores, 0)
      this.resultCountTarget.textContent = `Showing ${rows.length} client${rows.length === 1 ? "" : "s"} · ${stores} stores · ${totalVisits} visits · ${totalCutins} cut-ins`
    }
  }

  sortRows(rows, view, valueFor) {
    const key = this.state.sortKey[view]
    const dir = this.state.sortDir[view] === "asc" ? 1 : -1

    return [...rows].sort((a, b) => {
      const va = valueFor(a, key)
      const vb = valueFor(b, key)
      if (va === vb) return 0
      if (va === null || va === undefined) return 1
      if (vb === null || vb === undefined) return -1
      if (typeof va === "string") return va.localeCompare(vb) * dir
      return (va - vb) * dir
    })
  }

  arrow(view, key) {
    if (this.state.sortKey[view] !== key) return ""
    return `<span class="cir-sort-arrow">${this.state.sortDir[view] === "asc" ? "▲" : "▼"}</span>`
  }

  sortableHeader(view, key, label, align = "") {
    return `<th class="table-header-primary cir-sortable ${align}" data-sort-key="${key}" data-action="click->cut-in-report#setSort">${label}${this.arrow(view, key)}</th>`
  }

  renderStoreTable(rows) {
    const sorted = this.sortRows(rows, "store", (r, key) => r[key])
    const natAvg = this.nationalAverageRatio(rows)

    const totals = rows.reduce((acc, r) => {
      acc.visits += r.visits
      acc.visits_with_cutin += r.visits_with_cutin
      acc.cut_ins += r.cut_ins
      return acc
    }, { visits: 0, visits_with_cutin: 0, cut_ins: 0 })
    const totalPct = totals.visits > 0 ? Math.round((totals.visits_with_cutin / totals.visits) * 1000) / 10 : null
    const totalRatio = totals.visits > 0 ? totals.cut_ins / totals.visits : null

    const body = sorted.map((r) => `
      <tr class="odd:bg-white even:bg-gray-50">
        <td class="table-row-primary cir-td-num">${r.rank ?? "—"}</td>
        <td class="table-row-primary cir-td-num"><a href="/places/${r.place_id}" class="cir-win-link">${r.place_id}</a></td>
        <td class="table-row-primary text-start"><a href="/places/${r.place_id}" class="cir-win-link font-medium text-gray-800">${r.store}</a></td>
        <td class="table-row-primary cir-rep-name">${r.rep}</td>
        <td class="table-row-primary"><span class="cir-state-pill">${r.state}</span></td>
        <td class="table-row-primary"><span class="cir-state-pill ${r.territory === "Tess" ? "cir-terr-tess" : r.territory === "Scott" ? "cir-terr-scott" : ""}">${r.territory}</span></td>
        <td class="table-row-primary cir-td-num ${r.visits === 0 ? "cir-td-zero" : ""}">${r.visits}</td>
        <td class="table-row-primary cir-td-num ${r.visits === 0 ? "cir-td-zero" : ""}">${r.visits === 0 ? "—" : r.visits_with_cutin}</td>
        <td class="table-row-primary cir-td-num ${this.srClass(r.strike_rate)}">${r.strike_rate === null ? "—" : r.strike_rate + "%"}</td>
        <td class="table-row-primary cir-td-num ${r.visits === 0 ? "cir-td-zero" : ""}">${r.visits === 0 ? "—" : r.cut_ins}</td>
        <td class="table-row-primary cir-td-num ${this.ratioClass(r.ratio, natAvg)}">${r.ratio === null ? "—" : r.ratio.toFixed(2)}</td>
        <td class="table-row-primary text-center cir-freq-cell">${this.freqLabel(r.frequency)}</td>
      </tr>
    `).join("")

    this.tableContainerTarget.innerHTML = `
      <table class="table-primary">
        <thead>
          <tr>
            ${this.sortableHeader("store", "rank", "RANK", "text-right")}
            ${this.sortableHeader("store", "place_id", "PLACE ID", "text-right")}
            ${this.sortableHeader("store", "store", "STORE NAME")}
            ${this.sortableHeader("store", "rep", "REP")}
            ${this.sortableHeader("store", "state", "STATE")}
            <th class="table-header-primary">TERRITORY</th>
            ${this.sortableHeader("store", "visits", "VISITS", "text-right")}
            ${this.sortableHeader("store", "visits_with_cutin", "VISITS W/ CUT-IN", "text-right")}
            ${this.sortableHeader("store", "strike_rate", "STRIKE RATE %", "text-right")}
            ${this.sortableHeader("store", "cut_ins", "CUT-INS", "text-right")}
            ${this.sortableHeader("store", "ratio", "CUT-INS/VISIT", "text-right")}
            <th class="table-header-primary text-center">FREQUENCY</th>
          </tr>
        </thead>
        <tbody class="table-body-primary">${body || `<tr><td colspan="12" class="table-row-primary text-center text-gray-400">No data for these filters</td></tr>`}</tbody>
        <tfoot class="cir-tfoot">
          <tr>
            <td colspan="6">TOTAL</td>
            <td class="cir-td-num">${totals.visits}</td>
            <td class="cir-td-num">${totals.visits_with_cutin}</td>
            <td class="cir-td-num">${totalPct === null ? "—" : totalPct + "%"}</td>
            <td class="cir-td-num">${totals.cut_ins}</td>
            <td class="cir-td-num">${totalRatio === null ? "—" : totalRatio.toFixed(2)}</td>
            <td></td>
          </tr>
        </tfoot>
      </table>
    `
  }

  renderPersonTable(rows) {
    const sorted = this.sortRows(rows, "person", (r, key) => r[key])
    const natAvg = this.nationalAverageRatio(rows)

    const body = sorted.map((r) => `
      <tr class="odd:bg-white even:bg-gray-50">
        <td class="table-row-primary text-start"><a href="/users/${r.user_id}" class="cir-win-link font-medium text-gray-800">${r.rep}</a></td>
        <td class="table-row-primary"><span class="cir-state-pill ${r.territory === "Tess" ? "cir-terr-tess" : r.territory === "Scott" ? "cir-terr-scott" : ""}">${r.territory}</span></td>
        <td class="table-row-primary cir-rep-name">${r.states}</td>
        <td class="table-row-primary cir-td-num">${r.stores}</td>
        <td class="table-row-primary cir-td-num">${r.visits}</td>
        <td class="table-row-primary cir-td-num ${r.visits === 0 ? "cir-td-zero" : ""}">${r.visits === 0 ? "—" : r.visits_with_cutin}</td>
        <td class="table-row-primary cir-td-num ${this.srClass(r.strike_rate)}">${r.strike_rate === null ? "—" : r.strike_rate + "%"}</td>
        <td class="table-row-primary cir-td-num">${r.cut_ins}</td>
        <td class="table-row-primary cir-td-num ${this.ratioClass(r.ratio, natAvg)}">${r.ratio === null ? "—" : r.ratio.toFixed(2)}</td>
      </tr>
    `).join("")

    this.tableContainerTarget.innerHTML = `
      <table class="table-primary">
        <thead>
          <tr>
            ${this.sortableHeader("person", "rep", "REP")}
            <th class="table-header-primary">TERRITORY</th>
            ${this.sortableHeader("person", "states", "STATES")}
            ${this.sortableHeader("person", "stores", "STORES", "text-right")}
            ${this.sortableHeader("person", "visits", "VISITS", "text-right")}
            ${this.sortableHeader("person", "visits_with_cutin", "VISITS W/ CUT-IN", "text-right")}
            ${this.sortableHeader("person", "strike_rate", "STRIKE RATE %", "text-right")}
            ${this.sortableHeader("person", "cut_ins", "CUT-INS", "text-right")}
            ${this.sortableHeader("person", "ratio", "CUT-INS/VISIT", "text-right")}
          </tr>
        </thead>
        <tbody class="table-body-primary">${body || `<tr><td colspan="9" class="table-row-primary text-center text-gray-400">No data for these filters</td></tr>`}</tbody>
      </table>
    `
  }

  renderClientTable(rows) {
    const sorted = this.sortRows(rows, "client", (r, key) => r[key])
    const natAvg = this.nationalAverageRatio(rows)

    const body = sorted.map((r) => `
      <tr class="odd:bg-white even:bg-gray-50">
        <td class="table-row-primary text-start font-medium text-gray-800">${r.client}</td>
        <td class="table-row-primary cir-td-num">${r.stores}</td>
        <td class="table-row-primary cir-td-num">${r.visits}</td>
        <td class="table-row-primary cir-td-num ${r.visits === 0 ? "cir-td-zero" : ""}">${r.visits === 0 ? "—" : r.visits_with_cutin}</td>
        <td class="table-row-primary cir-td-num ${this.srClass(r.strike_rate)}">${r.strike_rate === null ? "—" : r.strike_rate + "%"}</td>
        <td class="table-row-primary cir-td-num">${r.cut_ins}</td>
        <td class="table-row-primary cir-td-num ${this.ratioClass(r.ratio, natAvg)}">${r.ratio === null ? "—" : r.ratio.toFixed(2)}</td>
      </tr>
    `).join("")

    this.tableContainerTarget.innerHTML = `
      <table class="table-primary">
        <thead>
          <tr>
            ${this.sortableHeader("client", "client", "CLIENT")}
            ${this.sortableHeader("client", "stores", "STORES", "text-right")}
            ${this.sortableHeader("client", "visits", "VISITS", "text-right")}
            ${this.sortableHeader("client", "visits_with_cutin", "VISITS W/ CUT-IN", "text-right")}
            ${this.sortableHeader("client", "strike_rate", "STRIKE RATE %", "text-right")}
            ${this.sortableHeader("client", "cut_ins", "CUT-INS", "text-right")}
            ${this.sortableHeader("client", "ratio", "CUT-INS/VISIT", "text-right")}
          </tr>
        </thead>
        <tbody class="table-body-primary">${body || `<tr><td colspan="7" class="table-row-primary text-center text-gray-400">No data for these filters</td></tr>`}</tbody>
      </table>
    `
  }

  // ---------- filter bar ----------

  renderFilterBar() {
    if (!this.hasFilterBarTarget) return
    const f = this.state.filters

    const segBtn = (filterKey, value, label, active) =>
      `<button type="button" class="cir-toggle-btn ${active ? "active" : ""}" data-filter-key="${filterKey}" data-filter-value="${value}" data-action="click->cut-in-report#setSegFilter">${label}</button>`

    const presetBtn = (preset, label, active) =>
      `<button type="button" class="cir-toggle-btn ${active ? "active" : ""}" data-preset="${preset}" data-action="click->cut-in-report#selectPeriodPreset">${label}</button>`

    const taskerOptions = Object.entries(this.data.taskers || {}).sort((a, b) => a[1].localeCompare(b[1]))
    const clientOptions = Object.entries(this.data.clients || {}).sort((a, b) => a[1].localeCompare(b[1]))
    const ownerOptions = Object.entries(this.data.place_owners || {}).sort((a, b) => a[1].localeCompare(b[1]))
    const monthOptions = this.data.cycles.map((c) => [c.cycle_id, c.label])
    const stateOptions = [...new Set(Object.values(this.data.places || {}).map((p) => p.state).filter(Boolean))].sort()

    const select = (filterKey, options, currentValue, allLabel = "All", action = "setSelectFilter") => `
      <select class="cir-select" data-filter-key="${filterKey}" data-action="change->cut-in-report#${action}">
        <option value="all" ${currentValue === "all" ? "selected" : ""}>${allLabel}</option>
        ${options.map(([value, label]) => `<option value="${value}" ${String(currentValue) === String(value) ? "selected" : ""}>${label}</option>`).join("")}
      </select>
    `

    const isAllPeriod = f.granularity === "cycle" && f.cycle === "all"
    const ctd = this.ctdCycleEntry()
    const thisWeek = this.thisWeekEntry()
    const lastWeek = this.lastWeekEntry()
    const isCtd = f.granularity === "cycle" && ctd && String(f.cycle) === String(ctd.cycle_id)
    const isThisWeek = f.granularity === "week" && thisWeek && String(f.week) === String(thisWeek.cycle_id)
    const isLastWeek = f.granularity === "week" && lastWeek && String(f.week) === String(lastWeek.cycle_id)

    this.filterBarTarget.innerHTML = `
      <div class="flex items-center gap-2">
        <span class="cir-filter-label">Period</span>
        ${presetBtn("all", "All", isAllPeriod)}
        ${ctd ? presetBtn("ctd", "CTD", isCtd) : ""}
        ${thisWeek ? presetBtn("this_week", "This Week", isThisWeek) : ""}
        ${lastWeek ? presetBtn("last_week", "Last Week", isLastWeek) : ""}
        ${select("month", monthOptions, f.granularity === "cycle" ? f.cycle : "all", "Pick a month…", "selectMonth")}
      </div>
      <div class="flex items-center gap-2">
        <span class="cir-filter-label">Source</span>
        ${segBtn("source", "field", "Field", f.source === "field")}
        ${segBtn("source", "phone", "Phone", f.source === "phone")}
        ${segBtn("source", "both", "Both", f.source === "both")}
      </div>
      <div class="flex items-center gap-2">
        <span class="cir-filter-label">Person</span>
        ${select("person", taskerOptions, f.person)}
      </div>
      <div class="flex items-center gap-2">
        <span class="cir-filter-label">State</span>
        ${select("state", stateOptions.map((s) => [s, s]), f.state)}
      </div>
      <div class="flex items-center gap-2">
        <span class="cir-filter-label">Territory</span>
        ${segBtn("territory", "both", "Both", f.territory === "both")}
        ${segBtn("territory", "tess", "Tess", f.territory === "tess")}
        ${segBtn("territory", "scott", "Scott", f.territory === "scott")}
      </div>
      <div class="flex items-center gap-2">
        <span class="cir-filter-label">Client</span>
        ${select("client", clientOptions, f.client)}
      </div>
      <div class="flex items-center gap-2">
        <span class="cir-filter-label">Place Owner</span>
        ${select("placeOwner", ownerOptions, f.placeOwner)}
      </div>
      <div class="flex items-center gap-2">
        <span class="cir-filter-label">Frequency</span>
        ${segBtn("frequency", "all", "All", f.frequency === "all")}
        ${segBtn("frequency", "28", "4-weekly", f.frequency === "28")}
        ${segBtn("frequency", "56", "8-weekly", f.frequency === "56")}
        ${segBtn("frequency", "other", "Other", f.frequency === "other")}
      </div>
      <div class="flex items-center gap-2">
        <span class="cir-filter-label">Rank</span>
        <input type="number" class="cir-rank-input" placeholder="From" value="${f.rankFrom}" data-filter-key="rankFrom" data-action="change->cut-in-report#setRankFilter">
        <input type="number" class="cir-rank-input" placeholder="To" value="${f.rankTo}" data-filter-key="rankTo" data-action="change->cut-in-report#setRankFilter">
      </div>
      <div class="flex items-center gap-2">
        <span class="cir-filter-label">Strike Rate</span>
        <button type="button" class="cir-toggle-btn ${f.strikeRate.length === 0 ? "active" : ""}" data-filter-value="all" data-action="click->cut-in-report#toggleStrikeRateFilter">All</button>
        ${this.strikeRateBuckets().map((b) => `<button type="button" class="cir-toggle-btn ${f.strikeRate.includes(b.key) ? "active" : ""}" data-filter-value="${b.key}" data-action="click->cut-in-report#toggleStrikeRateFilter">${b.label}</button>`).join("")}
      </div>
      <div class="flex items-center gap-2">
        <span class="cir-filter-label">Store</span>
        <input type="text" class="cir-store-search" placeholder="Search store name…" value="${f.store}" data-action="input->cut-in-report#setStoreSearch">
      </div>
    `
  }

  // ---------- CSV export ----------

  buildCsv() {
    const rows = this.lastRenderedRows || []
    let headers
    let lines

    if (this.state.view === "store") {
      headers = ["Rank", "Place ID", "Store", "Rep", "State", "Territory", "Visits", "Visits w/ Cut-in", "Strike Rate %", "Cut-ins", "Cut-ins per Visit", "Frequency"]
      lines = rows.map((r) => [r.rank ?? "", r.place_id, r.store, r.rep, r.state, r.territory, r.visits, r.visits_with_cutin, r.strike_rate ?? "", r.cut_ins, r.ratio?.toFixed(2) ?? "", this.freqLabel(r.frequency)])
    } else if (this.state.view === "person") {
      headers = ["Rep", "Territory", "States", "Stores", "Visits", "Visits w/ Cut-in", "Strike Rate %", "Cut-ins", "Cut-ins per Visit"]
      lines = rows.map((r) => [r.rep, r.territory, r.states, r.stores, r.visits, r.visits_with_cutin, r.strike_rate ?? "", r.cut_ins, r.ratio?.toFixed(2) ?? ""])
    } else {
      headers = ["Client", "Stores", "Visits", "Visits w/ Cut-in", "Strike Rate %", "Cut-ins", "Cut-ins per Visit"]
      lines = rows.map((r) => [r.client, r.stores, r.visits, r.visits_with_cutin, r.strike_rate ?? "", r.cut_ins, r.ratio?.toFixed(2) ?? ""])
    }

    const escapeCell = (cell) => {
      const str = String(cell ?? "")
      return /[",\n]/.test(str) ? `"${str.replace(/"/g, '""')}"` : str
    }

    return [headers, ...lines].map((row) => row.map(escapeCell).join(",")).join("\n")
  }
}
